import * as Yup from 'yup';
import { ADDRESS_FIELD } from '../helper/constants';
import {
    checkIfAddressFieldIsBlocked,
    isUSA,
    isValidName,
    isValidPhoneNumber,
} from 'utils/utilities';

function equalTo(ref, msg) {
    return Yup.mixed().test({
        name: 'equalTo',
        exclusive: false,
        message: msg || 'Path must be the same as Reference',
        params: {
            reference: ref.path,
        },
        test: function (value) {
            return value === this.resolve(ref);
        },
    });
}

Yup.addMethod(Yup.string, 'equalTo', equalTo);

const validationSchema = ({
    ssnConfig,
    userDetails,
    mandatoryFields,
    isInformationMismatched,
    askForFallback,
    isRiskAssessmentRoute,
    ...props
}) => {
    const isSsnRequired =
        !isInformationMismatched && Boolean(ssnConfig?.type) && !userDetails?.is_ssn_available;
    const isSsnFallbackRequired = !isInformationMismatched && askForFallback;

    const { addressBlocked, zipCodeBlocked } = checkIfAddressFieldIsBlocked({
        companyUUID: props?.company?.uuid,
        mandatoryFields,
        isRiskAssessmentRoute,
        userDetails: props?.userDetails,
    });

    const conditionallyRequiredAddressField = (field) => {
        if (isRiskAssessmentRoute) {
            if (field === 'zipCode' && !zipCodeBlocked) {
                return Yup.string().required('Required');
            }

            return addressBlocked ? Yup.string() : Yup.string().required('Required');
        }

        if (ADDRESS_FIELD.includes(field) && mandatoryFields.includes('address')) {
            return Yup.string().required('Required');
        }

        return Yup.string().nullable();
    };

    const shouldRequireSSN = (country) => {
        return (!country || isUSA(country)) && (isSsnRequired || isSsnFallbackRequired);
    };

    return Yup.object().shape({
        firstName: Yup.string()
            .test('is-valid-name', 'Invalid first name', isValidName)
            .required('Required'),

        middleName: Yup.string()
            .nullable()
            .test('is-valid-name', 'Invalid middle name', isValidName)
            .when('acceptMiddleName', (acceptMiddleName, schema) => {
                if (acceptMiddleName === false || acceptMiddleName === null) {
                    return schema.required('Required');
                }
                return schema;
            }),

        lastName: Yup.string()
            .test('is-valid-name', 'Invalid last name', isValidName)
            .required('Required'),

        dob: Yup.string().nullable().required('Required'),

        ssn: Yup.string()
            .min(4, 'Invalid SSN')
            .when('country', function (country) {
                return shouldRequireSSN(country) ? this.required('Required') : this;
            }),

        confirmSSN: Yup.string()
            .min(4, 'Invalid SSN')
            .when('ssn', {
                is: (ssn) => !!ssn?.length,
                then: (schema) =>
                    schema
                        .required('Please confirm the SSN')
                        .oneOf([Yup.ref('ssn')], 'SSN must match'),
            })
            .when('country', function (country) {
                return shouldRequireSSN(country) ? this.required('Required') : this;
            }),

        houseNumber: conditionallyRequiredAddressField('houseNumber'),
        streetName: conditionallyRequiredAddressField('streetName'),
        city: conditionallyRequiredAddressField('city').test(
            'is-valid-city',
            'Please enter a valid city',
            isValidName
        ),
        zipCode: conditionallyRequiredAddressField('zipCode'),
        state: conditionallyRequiredAddressField('state').test(
            'is-valid-state',
            'Please enter a valid state',
            isValidName
        ),
        country: conditionallyRequiredAddressField('country').test(
            'is-valid-country',
            'Please enter a valid country',
            isValidName
        ),

        address: Yup.string().when('country', {
            is: function (country) {
                return isUSA(country) && mandatoryFields.includes('address');
            },
            then: Yup.string().required('Required'),
            otherwise: Yup.string(),
        }),

        email: Yup.string().required('Required').email('Please enter valid email.'),

        acceptMiddleName: Yup.boolean().nullable(),

        phone: mandatoryFields.includes('phone_number')
            ? Yup.string()
                  .nullable()
                  .required('Required')
                  .test('is-valid-phone-number', 'Invalid phone number', isValidPhoneNumber)
            : Yup.string().nullable(),

        // Optional in every country — only multi-unit addresses have a unit number,
        // so this can never be a required field.
        apartmentNumber: Yup.string().nullable(),
    });
};

export default validationSchema;
